import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { X, Edit2, Menu } from 'lucide-react';
import { ConfirmDialog } from '../modals';
import EditTaskForm from './EditTaskForm';
import TaskItem from './TaskItem';
import { FILTERS } from '../../constants';

// タスクカードコンポーネント
const TaskCard = ({
  task,
  isTeacher,
  onDeleteTask,
  onUpdateTask,
  onCheckItem,
  onTeacherCheckItem,
  onPassFailItem,
  onUpdateItem,
  onDeleteItem,
  getStudentId,
  selectedSubject,
  currentUser,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(FILTERS[0]);

  return (
    <div className="p-4 mb-6 bg-yellow-50 rounded-lg shadow">
      {isEditing ? (
        <EditTaskForm
          task={task}
          onSave={(updatedTask) => {
            onUpdateTask(updatedTask);
            setIsEditing(false);
          }}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <>
          {/* 課題のタイトルとドラッグハンドル */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              {isTeacher && (
                <span
                  {...task.dragHandleProps}
                  className="mr-2 cursor-grab text-purple-500 hover:text-purple-700"
                  aria-label="課題をドラッグ"
                >
                  <Menu size={22} />
                </span>
              )}
              <div className="font-semibold text-lg text-purple-700">
                {task.title}
              </div>
            </div>
            {isTeacher && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-purple-500 hover:text-purple-700"
                  aria-label="課題を編集"
                >
                  <Edit2 size={22} />
                </button>

                <button
                  onClick={() => {
                    setConfirmDialog({
                      title: '課題の削除',
                      message: 'この課題を削除してもよろしいですか？',
                      onConfirm: () => {
                        onDeleteTask();
                        setConfirmDialog(null);
                      },
                      onCancel: () => setConfirmDialog(null),
                    });
                  }}
                  className="text-pink-500 hover:text-pink-700"
                  aria-label="課題を削除"
                >
                  <X size={22} />
                </button>
              </div>
            )}
          </div>

          {/* フィルターの選択UI */}
          <div className="mb-4">
            <select
              value={selectedFilter.id}
              onChange={(e) => {
                const filter = FILTERS.find((f) => f.id === e.target.value);
                setSelectedFilter(filter);
              }}
              className="w-full p-2 border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-300"
              aria-label="課題フィルター選択"
            >
              {FILTERS.map((filter) => (
                <option key={filter.id} value={filter.id}>
                  {filter.name}
                </option>
              ))}
            </select>
          </div>

          {/* 折りたたみボタン */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="mb-4 text-purple-500 hover:text-purple-600 flex items-center gap-1 focus:outline-none transition-colors duration-200"
            aria-label={
              isCollapsed ? 'チェック項目を展開' : 'チェック項目を折りたたむ'
            }
            aria-expanded={!isCollapsed}
            aria-controls={`task-items-${task.id}`}
          >
            {isCollapsed ? 'チェック項目を表示' : 'チェック項目を非表示'}
          </button>

          {/* チェック項目の表示 */}
          {!isCollapsed && (
            <Droppable droppableId={`items-${task.id}`} type={`taskItems-${task.id}`}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-4"
                >
                  {Object.entries(task.items || {})
                    .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
                    .map(([itemId, item], index) => (
                      <Draggable
                        key={itemId}
                        draggableId={`${task.id}-${itemId}`}
                        index={index}
                        isDragDisabled={!currentUser.isMaster}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              opacity: snapshot.isDragging ? 0.8 : 1
                            }}
                          >
                            <TaskItem
                              item={{ ...item, id: itemId }}
                              isTeacher={isTeacher}
                              onCheck={() => onCheckItem(task.id, itemId)}
                              onTeacherCheck={(status) =>
                                onTeacherCheckItem(task.id, itemId, status)
                              }
                              onPassFail={(status) =>
                                onPassFailItem(task.id, itemId, status)
                              }
                              onUpdateItem={(updatedItem) =>
                                onUpdateItem(task.id, itemId, updatedItem)
                              }
                              onDeleteItem={() => onDeleteItem(task.id, itemId)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}

          {/* 確認ダイアログ */}
          {confirmDialog && (
            <ConfirmDialog
              title={confirmDialog.title}
              message={confirmDialog.message}
              onConfirm={confirmDialog.onConfirm}
              onCancel={confirmDialog.onCancel}
            />
          )}
        </>
      )}
    </div>
  );
};

TaskCard.propTypes = {
  task: PropTypes.object.isRequired,
  isTeacher: PropTypes.bool.isRequired,
  onDeleteTask: PropTypes.func.isRequired,
  onUpdateTask: PropTypes.func.isRequired,
  onCheckItem: PropTypes.func.isRequired,
  onTeacherCheckItem: PropTypes.func.isRequired,
  onPassFailItem: PropTypes.func.isRequired,
  onUpdateItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  getStudentId: PropTypes.func.isRequired,
  selectedSubject: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
};

export default memo(TaskCard); 